import React, { useEffect, useMemo, useRef, useState} from 'react';
import {history, shallowEqual, useDispatch, useSelector, useIntl} from 'umi';
import {useDebounceFn, useRequest, useUpdateEffect} from 'ahooks';
import {
  Badge,
  Bubble,
  Button,
  Card,
  ConfigProvider,
  Dropdown,
  Justify,
  Layout,
  List,
  notification,
  PagingQuery,
  SearchBox,
  Segment,
  Table,
  Text
} from 'tea-component';
import classNames from 'classnames';
import { deleteDashboard, getDashboardList, publishDashboard } from '@dbl/services/dashboard';
import {useDblIntl} from '@/pages/datalab/utils/utils';
import PublishModal from '../components/PublishModal';
import DeleteDashboardModal from '../components/DeleteDashboardModal';
import UnPublishDashboardModal from '../components/UnPublishDashboardModal';
import TextBubbles from '@/pages/home/components/TextBubbles';
import {buriedPoint} from '@home/utils/buriedPoint';
import AccessSettingModal, {Editor} from '@/pages/datalab/components/AccessSettingModal';
import BubbleEllipsis from '@/pages/datalab/components/BubbleEllipsis';
import {GlobalPositioningGuidance, SHARE_DASHBOARD} from '../../Home/GettingStart';
import {useGetDashBoardCreatorData, useGetDataLabGuideDataList, useGetHomeTabCount} from '../../Home/hoooks';
import {isArray, isEmpty} from 'lodash-es';
import FullLoading from '@/pages/home/components/FullLoading';
import {DATALAB_EDITOR} from '@dbl/core/global';
import {useStore} from '@/pages/aigc/hooks/useContext/useStore';
import SvgIcon from '@home/components/SvgIcon';
import {TableDataTab} from '../constants';
import SetAsTemplateModal from '../components/SetAsTemplateModal';
import {TableHoverFiledSearchBox} from '../../Home/RecentlyAndShare';
import {getShowCount} from '@/pages/datalab/utils/common';
import {getIsTemplatePageFn} from '../CreateReport/utils';
import CopyDashboardModal, {
  ChartInDashboardCopyType
} from '@/pages/datalab/pages/WorkBench/components/CopyDashboardModal';
import './index.scss';

const { Body, Content } = Layout;
const { pageable, sortable, scrollable, autotip } = Table.addons;
export const PAGE_INFO_INIT = { pageSize: 10, pageIndex: 1 };
const DEFAULT_SORT = { by: 'create_time', order: 'desc' };
function MyDashboard() {
  // 当前排序列
  const [sort, setSort] = useState<any>([
    DEFAULT_SORT
  ]);
  // 搜索框受控value
  const [searchValue, setSearchValue] = useState<string | undefined>('');
  // 搜索框触发请求的value
  const [searchWords, setSearchWords] = useState<string | undefined>('');
  const [deleteRecord, setDeleteRecord] = useState<any>();
  const [publishRecord, setPublishRecord] = useState<any>();
  const [unPublishRecord, setUnPublishRecord] = useState<any>();
  const [templateRecord, setTemplateRecord] = useState<any>();
  const [pageInfo, setPageInfo] = useState(PAGE_INFO_INIT);
  const [showPublishModal, setShowPublishModal] = useState<boolean>(false);
  const [showUnPublishModal, setShowUnPublishModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showSetTemplateModal, setShowSetTemplateModal] = useState<boolean>(false);
  const [showCopy, setShowCopy] = useState<boolean>(false);
  const [copyDashboard, setCopyDashboard] = useState<any>();

  const [accessInfo, setAccessInfo] = useState<{ id: number; editors: Editor[]; creatorId: string; }>({
    id: -1,
    editors: [],
    creatorId: ''
  }); // 权限弹窗需要的信息
  const [dashboardTab,setDashboardTab] = useState<TableDataTab>(TableDataTab.Dashboard);
  const [creators, setCreators] = useState<any[]>([]);
  const [publishStatuses, setPublishStatuses] = useState<number[]>([]);
  const gameCode = useSelector(state => state.datalab.gameCode, shallowEqual);
  // 保存上次code
  const [lastCode, setLastCode] = useStore<string>('lastCodeDash', gameCode);
  
  const [searchQuery, setSearchQuery] = useStore<{[key in string]:unknown }>(
    'searchQuery',
    {},
  );
  
  useEffect(()=>()=>{
    setLastCode(gameCode);
  },[gameCode]);
  // 请求放在父组件，避免切换tab时重复请求
  const initialFilterRef = useRef<{ creator: string[]; publishStatus: number[]; }>({
    creator: [],
    publishStatus: [],
  });
  useEffect(() => {
    // 先读取缓存数据
    const {pageInfo: cachedPageInfo, dashboardSort, keyword='', tab, creator, publishStatus} = searchQuery || {};
    const initialCreator = (creator as string[]) ?? [];
    const initialPublishStatus = (publishStatus as number[]) ?? [];
    const normalizedPageInfo = (cachedPageInfo && typeof cachedPageInfo === 'object')
      ? cachedPageInfo as { pageSize?: number; pageIndex?: number; }
      : undefined;
    setSearchValue(keyword as string );
    setSearchWords(keyword as string );
    setPageInfo({
      pageSize: normalizedPageInfo?.pageSize ?? PAGE_INFO_INIT.pageSize,
      pageIndex: normalizedPageInfo?.pageIndex ?? PAGE_INFO_INIT.pageIndex,
    });
    setSort(!isEmpty(dashboardSort)? dashboardSort : [DEFAULT_SORT]);
    setCreators(initialCreator);
    setPublishStatuses(initialPublishStatus);
    initialFilterRef.current = {
      creator: initialCreator,
      publishStatus: initialPublishStatus,
    };
    setDashboardTab(tab || TableDataTab.MyDashboard);
    // 读取完,直接clear缓存
    setSearchQuery({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameCode, lastCode]);
  
  const { dblFormatMessage, commonFormatMessage, isEn } = useDblIntl('dashboard_list');
  const countInitLoadingRef = useRef(true); // 页面初始化进入时, 请求count的loading; 初始化结束之后为false
  
  // 获取创建人列表
  const { data: createList, run: runCreators, loading: createLoading  } = useGetDashBoardCreatorData(useMemo(()=>({tab: dashboardTab, game_code: gameCode }),[dashboardTab, gameCode]));
  
  // 获取各页签总数
  const { data: tabCount, run: runTabCountData, refresh: refreshTabCountData } = useGetHomeTabCount(useMemo(() => ({ game_code: gameCode, tab: 'dashboard'}), [gameCode]), (data) => {
    if (countInitLoadingRef.current) {
      countInitLoadingRef.current = false;
      let tab = dashboardTab;
      
      if (data.my_dashboard_count < 1 && dashboardTab === TableDataTab.MyDashboard) {
        tab = TableDataTab.Dashboard;
        setDashboardTab(tab);
      }
      
      
      runCreators();
      const { creator, publishStatus } = initialFilterRef.current;
      getTableListData(
        pageInfo,
        sort,
        searchValue,
        tab,
        creator,
        publishStatus,
      );
      // setDashboardTab(tab);
    }
  });

  const dispatch = useDispatch();
  useEffect(() => {
    buriedPoint({
      pageId: '601101',
      pageName: '我的报表',
      extInfo: `{"pageName": "My Dashboard", "pageNameCn": "我的报表", "id": ""}`,
      pagePath: '/WorkBench/MyDashboard',
      uid: gameCode,
      typeId: 'datalab_pc_v2',
      menuId: '0',
    });
  }, [gameCode]);

  const requestDashboardList = (
    pageMsg = pageInfo,
    sortInfo = sort,
    keyword = searchValue,
    tab: string,
    creator = creators,
    status = publishStatuses,
  ) => {
    const { pageSize, pageIndex } = pageMsg;
    const params = {
      game_code: gameCode,
      keyword,
      sort: sortInfo?.map(task=>({key: task?.by, value: task?.order})),
      page: pageIndex,
      page_size: pageSize,
      tab: tab || dashboardTab,
      creators: creator || [],
      publish_statuses: status || [],
    };
    return getDashboardList(params);
  };

  const {
    data,
    loading,
    error,
    run: getTableListData,
    refresh: refreshTableListData,
    // mutate
  }: any = useRequest(requestDashboardList, {
    manual: true,
    // 请不要随意修改这个key，全局上下文唯一key,后来者需要注意，使用这个hooks，返回的Promise是贯穿上下文的，也就是说只要run一遍，在组件的各个地方都能拿到最新返回值，把这个hooks放置在顶层，遵循数据自上而下流动
    cacheKey: SHARE_DASHBOARD,
    onSuccess: () => {
      // const mapKey = {
      //   "share": 'share_count',
      //   "dashboard": "dashboard_count",
      //   "game_template": "game_template_count",
      //   "public_template": "public_template_count",
      //   "my_dashboard": "my_dashboard_count",
      // };
      // const currentTabKey = mapKey[dashboardTab];
      // tabCountMutate({...tabCount||{},[currentTabKey]: res?.total_count});
    },
    onError: e => {
      console.log(e);
    },
  });
  
  const { defaulet, guideSubTab, isShowGuide } = useGetDataLabGuideDataList();
  const { run: deleteDashboardItem } = useRequest(params => deleteDashboard(params), {
    manual: true,
    onSuccess: () => {
      // 发布成功通知
      notification.success({
        description: commonFormatMessage('delete_successfully'),
        duration: 1000
      });
      // 在页签 All 和 页签 My Dashboard 下删除报表时, 重新请求count接口总数
      [TableDataTab.Dashboard, TableDataTab.MyDashboard].includes(dashboardTab) && runTabCountData({game_code: gameCode, tab: dashboardTab, creators});
      // 如果删除的是当前页最后一条数据，则重置到第一页
      const isReset = pageInfo?.pageIndex > 1 && (data?.total_count - 1 === (pageInfo?.pageIndex - 1) * pageInfo?.pageSize);
      isReset && setPageInfo({ pageSize: pageInfo?.pageSize, pageIndex: 1 });
      getTableListData(
        isReset ? { ...pageInfo, pageIndex: 1 } : pageInfo,
        sort,
        searchValue,
        dashboardTab,
        creators,
        publishStatuses,
      );
    },
    onError: () => {
      // 发布失败通知
      notification.error({
        description: commonFormatMessage('delete_failed'),
        duration: 1000
      });
    }
  });
  // 忽略初次更新，跟随这几个状态实时set
  useUpdateEffect(() => {
  // 当点击指定的按钮时，触发接口缓存，防止路由离开状态被清空
    searchQuery?.isCatch && setSearchQuery({
      keyword: searchValue,
      pageInfo,
      dashboardSort: sort,
      tab: dashboardTab,
      creator: creators,
      publishStatus: publishStatuses,
    });
  },[searchValue, pageInfo, sort, searchQuery?.isCatch, dashboardTab, creators, publishStatuses]);

  // 规则
  // 1：先展示列表第一项符合条件的(一定要有权限+发布 || 权限+撤销发布)，进行高亮
  // 2：如果没有符合条件高亮的，插入一条假数据，，然后关闭之后展示真实数据
  const tableRecordsClassNamesFn=(dataList: any)=>{
    if(isEmpty(dataList)|| !isArray(dataList)) return ''; 
    return  dataList?.find(task=> task.publish && ([0].includes(task.publish_status) || task.publish_status) )?.id;
  };
  const isShowActionsFieldsList = !getIsTemplatePageFn(dashboardTab);
  const tableRecords = useMemo(() => {
    // 如果没有拉取到数据并且是路由路径包含引导key
    if (isEmpty(data?.list) && guideSubTab) {
      return defaulet;
    }
    // 如果拉取到数据，但是操作栏这一侧没有权限以及发布功能，需要高亮默认假数据，在关闭指引时，数据源要还原成真实的，特殊处理一遍
    if( !isEmpty(data?.list) &&  !tableRecordsClassNamesFn(data?.list) && guideSubTab ) {
      return defaulet;
    }
    // 其他情况直接返回原数据源
    return data?.list || [];
  }, [data?.list, defaulet, guideSubTab]);
  const tableRecordsClassNames=useMemo(()=>tableRecordsClassNamesFn(tableRecords),[tableRecords]);
  // 报表(不是模板)的表格列表的右侧操作列的宽度actionColumnWidth
  // 原本的方法给操作列中的更多设置绝对定位的方案会让横向滚动到最后的时候, 表头和列内容左侧无法对齐.
  // 改成现在的方案, 同时保留 原本的展示不变.
  const actionColumnWidth = useMemo(() => {
    if (isEmpty(tableRecords) || !isArray(tableRecords)) return 50;
    // 如果没有发布权限，包含撤销发布
    const isAllUnpublished = tableRecords?.every(task => !task.publish);
    // 在发布权限的基础上，再次判断是发布还是撤销发布
    const isPublishedState = tableRecords?.some(task => task.publish_status !== 0);
    let left;
    if (isEn) {
      if (!isAllUnpublished) { // 所有报表都有发布权限.
        if (!isPublishedState) { // 没有一个是撤销发布
          left = 192;
        } else { // 有撤销发布
          left = 210;
        }
      } else { // 所有报表都没有发布权限.
        left = 130;
      }
    } else {
      if (!isAllUnpublished) {
        if (!isPublishedState) {
          left = 170;
        } else {
          left = 192;
        }
      } else {
        left = 125;
      }
    }
    return left;
  }, [isEn, tableRecords]);


  const renderCreatesOptions = useMemo(
    () => {
      if(createLoading) return [];
      return  createList?.map((item) => ({
        value: item?.user_id,
        text: item?.user_name,
        title: item?.title,
      }));
    },
    [createLoading, createList],
  );
  const {run: handleOnClickCreateSearch} = useDebounceFn((creators)=>{
    setCreators(creators);
    setPageInfo({pageSize: pageInfo.pageSize, pageIndex: 1}); // 筛选创建人时, 要回到第一页, 避免筛选数量 在当前页数下不存在.
    getTableListData(
      {pageSize: pageInfo.pageSize, pageIndex: 1},
      sort,
      searchValue,
      dashboardTab,
      creators,
      publishStatuses,
    );
    // 做缓存
    handleClickCatchValue();
  },{
    wait: 500
  });
  const renderPublishStatusOptions = useMemo(
    () => ([
      {
        value: 0,
        text: commonFormatMessage('un_published'),
      },
      {
        value: 1,
        text: commonFormatMessage('published'),
      },
    ]),
    [commonFormatMessage],
  );
  const {run: handleOnClickPublishStatusSearch} = useDebounceFn((statuses: number[])=>{
    setPublishStatuses(statuses);
    setPageInfo({pageSize: pageInfo.pageSize, pageIndex: 1});
    getTableListData(
      {pageSize: pageInfo.pageSize, pageIndex: 1},
      sort,
      searchValue,
      dashboardTab,
      creators,
      statuses,
    );
    handleClickCatchValue();
  },{
    wait: 500
  });

  const handleClickItemAccess = (cvm) => {
    const editors: Editor[] = [];
    cvm?.editable_person?.forEach((person) => {
      if (person.user_id === cvm.user_id) return;
      editors.push({
        userName: person?.user_name,
        userId: person?.user_id,
        isView:
          person?.permission?.length === 1
            ? person?.permission?.includes('read')
            : person?.permission?.filter((access) => access === 'read')
              ?.length === 2,
        isDevelop: person?.permission?.length > 1,
      });
    });
    setAccessInfo({
      id: cvm.id,
      editors,
      creatorId: cvm.user_id,
    });
  };

  const handleClickItemDelete = (cvm) => {
    setDeleteRecord(cvm);
    setShowDeleteModal(true);
  };
  const tableColunms = useMemo(
    () => {
      
      const columns = [
        {
          key: isShowActionsFieldsList ? 'dashboard_title' : 'template_title' ,
          header: dblFormatMessage('dashboard_name'),
          width: 320,
          fixed: 'left',
          render: (cvm: any) => (
            <TextBubbles
              className={cvm.read ? 'dbl-mydashboard-table-name' : ''}
              text={isShowActionsFieldsList ? cvm?.dashboard_title : cvm?.template_title}
              onClick={() => {
                handleClickCatchValue();
                const title = isShowActionsFieldsList ? cvm?.dashboard_title : cvm?.template_title;
                cvm.read &&
                history.push({
                  pathname: `/datalab/${gameCode}/WorkBench/MyDashboard/ViewPage`,
                  query: {
                    dashboardId: cvm?.id ?? '',
                    dashboardName: title ?? '',
                    dashboardStatus: cvm?.publish_status ?? '',
                    isTemplateReport: !isShowActionsFieldsList,
                  }
                });
                
              }}
            />
          ),
        },
        
        {
          key: 'create_time',
          header: commonFormatMessage('create_time'),
          width: 190,
          render: (cvm: any) => <div>{cvm?.create_time || '-'}</div>,
        },
        {
          key: 'user_name',
          header: <TableHoverFiledSearchBox {
            ...{
              gameCode,
              data: renderCreatesOptions,
              handleOnClickCreateSearch,
              value: creators,
              tab: dashboardTab,
              buttonText: commonFormatMessage('creator'),
            }
          }/>,
          width: 130,
          render: (cvm: any) => <div>{cvm?.user_name || '-'}</div>,
        },
        {
          key: 'update_user_name',
          header: commonFormatMessage('last_updater'),
          width: isEn ?  160 : 140,
          render: (cvm: any) => cvm?.update_user_name || '-',
        },
        {
          key: 'editable_person',
          header: commonFormatMessage('editable_person'),
          width: 150,
          render: (cvm: any) => {
            // eslint-disable-next-line eqeqeq
            const names = cvm?.editable_person_name?.filter(name => name != cvm.user_name)?.join(',');
            return <BubbleEllipsis content={names || '-'} />;
          }
        },
        {
          key: '',
          header: commonFormatMessage('operation'),
          fixed: 'right',
          width: isShowActionsFieldsList ? actionColumnWidth : 200,
          render: (cvm: any) => (
            <>
              <div  
                className='dbl-analysis-edit-actions-container'
                style={{
                  maxWidth: (isShowActionsFieldsList ? actionColumnWidth : 200) - 40,
                }}
              >
                <div
                  className={classNames({
                    "db-datalab-guide-btn": tableRecordsClassNames===cvm?.id
                  })}
                  style={{
                    display: 'inline-block',
                    marginRight: '12px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Button
                    disabled={!cvm?.edit}
                    type="link"
                    onClick={() => {
                      const title = isShowActionsFieldsList ? cvm?.dashboard_title : cvm?.template_title;
                      handleClickCatchValue();

                      dispatch({
                        type: 'datalab/save',
                        payload: {
                          isExpendLeftMenu: false,
                        }
                      });
                      history.push({
                        pathname: `/datalab/${gameCode}/WorkBench/MyDashboard/CreateDashboard`,
                        query: {
                          dashboardId: cvm?.id || '',
                          dashboardName: title || '',
                          isTemplateReport: String(!isShowActionsFieldsList)
                        }
                      });
                    }}
                  >
                    {commonFormatMessage('edit')}
                  </Button>
                  {cvm.publish  && (
                    cvm.publish_status === 0 ? (
                      <Button
                        type="link"
                        onClick={() => {
                          setPublishRecord(cvm);
                          setShowPublishModal(true);
                        }}
                      >
                        {commonFormatMessage('publish')}
                      </Button>
                    ) : (
                      <Button
                        type="link"
                        // eslint-disable-next-line @typescript-eslint/no-misused-promises
                        onClick={async () => {
                          setUnPublishRecord(cvm);
                          setShowUnPublishModal(true);
                        }}
                        loading={true}
                      >
                        {commonFormatMessage('un_publish')}
                      </Button>
                    )
                  )}
                </div>
                {
                  !isShowActionsFieldsList &&  <>
                    {
                      cvm.access &&  <Button
                        className="table-btn"
                        type="link"
                        onClick={()=>handleClickItemAccess(cvm)}
                      >
                        {commonFormatMessage('access')}
                      </Button>
                    }
                    {
                      cvm.copy && (<Button
                        className="table-btn"
                        type="link"
                        onClick={()=>{
                          setCopyDashboard(cvm);
                          setShowCopy(true);
                        }}
                      >
                        {commonFormatMessage('copy')}
                      </Button>)

                    }
                    {
                      cvm.publish_status === 0 && cvm.delete && <Button
                        type="link"
                        onClick={()=>handleClickItemDelete(cvm)}
                      >
                        {commonFormatMessage('delete')}
                      </Button>
                    }
                  </>
                }
                {
                  isShowActionsFieldsList &&  <Dropdown
                    // style={{
                    //   设置 position会让横向滚动到最后的时候, 表头和列内容左侧无法对齐.
                    //   position: 'absolute',
                    //   // left: actionColumnWidth
                    //   left: 108,
                    // }}
                    trigger='click'
                    boxClassName={'dbl-analysis-edit-actions-container-dropdown'}
                    placement="bottom-end"
                    button={ <>
                      { (cvm.access || cvm?.edit || cvm.delete || cvm.copy) && <div className='dbl-analysis-edit-actions-container-dropdown-item'>
				        <div> {dblFormatMessage('more') }</div>
				        <SvgIcon type="fill-icon-arrow"/>
			        </div>}
                    </>
                    }
                    appearance='pure'
                  >
                    {
                      () => (
                        <List type="option">
                          {
                            cvm.access && <List.Item  onClick={()=>handleClickItemAccess(cvm)} >
						        <Button
							        className="table-btn"
							        type="link"
						        >
                                {commonFormatMessage('access')}
						        </Button>
					        </List.Item>
                          }
                          { isShowActionsFieldsList && cvm?.edit &&  <List.Item  onClick={() => {
                          }}>
                            {
                              <Bubble
                                placement="left"
                                dark
                                content={dblFormatMessage('save_template_icon_info')}
                              >
                                <Button
                                  type="link"
                                  onClick={()=>{
                                    setShowSetTemplateModal(true);
                                    setTemplateRecord(cvm);
                                  }}
                                >
                                  {dblFormatMessage('set_template')}
                                </Button>
                              </Bubble>
                            }
				        </List.Item>
                          }
                          { cvm.copy &&
                            (<List.Item  onClick={()=>{
                              setCopyDashboard(cvm);
                              setShowCopy(true);
                            }}>
                              <Button
                                className="table-btn"
                                type="link"
                              >
                                {commonFormatMessage('copy')}
                              </Button>
                            </List.Item>)

                          }
                          {  cvm.publish_status === 0 && cvm.delete &&  <List.Item  onClick={()=>handleClickItemDelete(cvm)}>
                            { (
                              <Button
                                type="link"
                              >
                                {commonFormatMessage('delete')}
                              </Button>
                            )}
				        </List.Item>
                          }
                        </List>
                      )
                    }
                  </Dropdown>
                }
              </div>
            </>
          ),
        },
      ];
      isShowActionsFieldsList && columns.splice(1, 0, {
        key: 'publish_status',
        header: <TableHoverFiledSearchBox {
          ...{
            gameCode,
            data: renderPublishStatusOptions,
            handleOnClickCreateSearch: handleOnClickPublishStatusSearch,
            value: publishStatuses,
            tab: dashboardTab,
            buttonText: commonFormatMessage('status'),
            listWidth: 180,
          }
        }/>,
        width: 140,
        render: (cvm: any) => (
          <div className='dbl-mydashboard-table-row'>
            <Badge className='dbl-mydashboard-table-badge' dot theme={+cvm.publish_status === 1 ? 'success' : 'warning'} />
            {+cvm.publish_status === 1 ? commonFormatMessage('published') : commonFormatMessage('un_published')}
          </div>
        )
      });
      return columns;
    },
    [isEn, data, createList, actionColumnWidth, renderCreatesOptions, renderPublishStatusOptions, publishStatuses ],
  );

  const clearSearchWords = () => {
    setSearchValue('');
    setSearchWords('');
  };

  const deleteDashboardFunc = () => {
    deleteDashboardItem({
      game_code: gameCode,
      id: deleteRecord.id
    });
  };

  const unPublishDashboardFunc = async () => {
    try {
      await publishDashboard({ publish_status: 0, game_code: gameCode, dashboard_custom_id: unPublishRecord?.id });
      refreshTableListData();
      notification.success({
        description: commonFormatMessage('un_publish_successfully', {name: unPublishRecord.dashboard_title}),
        duration: 3000
      });
    } catch (e) {
      console.error('撤销发布失败', e);
      notification.error({
        description: commonFormatMessage('un_publish_failed', { code: e?.code || '' }),
        duration: 3000
      });
    }
  };

  const initTableParams = () => {
    setCreators([]);
    setPublishStatuses([]);
    setPageInfo({pageSize: pageInfo.pageSize, pageIndex: 1});
    setSort([DEFAULT_SORT]);  
  };

  const handleClickCatchValue = ()=>{
    setSearchQuery((res)=>({...res,isCatch: true}));
  };

  const handleOnClickTab = (value: TableDataTab) => {
    if(value === dashboardTab) return;
    // 更新count接口总数
    // runTabCountData({ game_code: gameCode, tab: value });

    runCreators();

    // 做缓存
    handleClickCatchValue();
    // 改变tab值
    setDashboardTab(value as TableDataTab);
    // 排序分页初始化
    initTableParams();
    // 搜索置空
    // clearSearchWords();
    // 调接口
    getTableListData(
      {pageSize: pageInfo.pageSize, pageIndex: 1},
      [DEFAULT_SORT],
      searchValue,
      value,
      [],
      [],
    );
  };

  // 搜索框onChange事件
  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    // 没有输入值时，相当于清空搜索
    if (value.length === 0) {
      handleOnClear();
    };
  };

  // 清空搜索：保留 Tab、排序与表格筛选，仅回到第一页并去掉关键字
  const handleOnClear = () => {
    setSearchValue('');
    setSearchWords('');
    setPageInfo({ pageSize: pageInfo.pageSize, pageIndex: 1 });
    getTableListData(
      { pageSize: pageInfo.pageSize, pageIndex: 1 },
      sort,
      '',
      dashboardTab,
      creators,
      publishStatuses,
    );
  };

  // 搜索：保留当前 Tab、排序与表格筛选（创建人 / 发布状态），仅回到第一页
  const handleOnSearch = (keyword: string | undefined) => {
    if (searchWords === keyword) return;
    if (keyword?.length === 0) {
      return handleOnClear();
    }
    setSearchValue(keyword);
    setSearchWords(keyword);
    setPageInfo({ pageSize: pageInfo.pageSize, pageIndex: 1 });
    getTableListData(
      { pageSize: pageInfo.pageSize, pageIndex: 1 },
      sort,
      keyword,
      dashboardTab,
      creators,
      publishStatuses,
    );
  };
  
  return (
    <Layout className='dbl-mydashboard-page'>
      { isEmpty(tableRecords) && guideSubTab && isShowGuide && <FullLoading /> || <GlobalPositioningGuidance/>}
      <Body>
        <Content>
          <Content.Header title={dblFormatMessage('my_dashboard')} />
          <Content.Body full>
            <Justify
              className='dbl-mydashboard-page-justify'
              left={<AuthPermissionDisablesContainers 
                type={DATALAB_EDITOR.AddDashboardBtn}
                showNoPermissionBubble
              >
                <Button
                  // className={'dbl-mydashboard-page-top-button'}
                  type='primary'
                  onClick={() => {
                    handleClickCatchValue();
                    history.push(`/datalab/${gameCode}/WorkBench/MyDashboard/CreateDashboard`);
                  }}
                >
                  {dblFormatMessage('new_dashboard')}
                </Button>
              </AuthPermissionDisablesContainers>}
              right={<SearchBox
                style={{ width: '440px' }}
                value={searchValue}
                onChange={handleSearchChange}
                onSearch={handleOnSearch}
                onClear={handleOnClear}
                placeholder={dblFormatMessage('search_dashboard')}
              />}
            />
            <Card>
              <Card.Body>
                <Justify
                  className='dbl-mydashboard-page-justify'
                  left={countInitLoadingRef.current ? '' :
                    <Segment
                      options={[
                        {
                          value: TableDataTab.Dashboard,
                          text: dblFormatMessage('tab_dashboard_no_count'),
                        },
                        {
                          value: TableDataTab.MyDashboard,
                          text: dblFormatMessage('tab_my_dashboard', {
                            count: getShowCount(tabCount?.my_dashboard_count || 0, true)
                          }),
                        },
                        {
                          value: TableDataTab.Share,
                          text: dblFormatMessage('tab_share_with_me', {
                            count: getShowCount(tabCount?.share_count || 0, true)
        
                          }),
                        },
                        {
                          value: TableDataTab.Game,
                          text: dblFormatMessage('tab_game_template', {
                            count: getShowCount(tabCount?.game_template_count || 0, true)
                          }),
                        },
                        {
                          value: TableDataTab.Public,
                          text: dblFormatMessage('tab_public_template', {
                            count: getShowCount(tabCount?.public_template_count || 0, true)
        
                          }),
                        },
                      ]}
                      value={dashboardTab}
                      onChange={handleOnClickTab}
                    />
                  }
                  // right={}
                />
                <ConfigProvider locale={isEn ? 'en' : 'zh'}>
                  <Table
                    className='dbl-mydashboard-page-table'
                    records={countInitLoadingRef.current || loading || error ? [] : tableRecords}
                    columns={tableColunms}
                    addons={[
                      pageable({
                        isPagingReset: true,
                        pageSize: pageInfo.pageSize,
                        pageIndex: pageInfo.pageIndex,
                        pageSizeOptions: [10, 20, 50, 100],
                        recordCount: data?.total_count || 0,
                        stateText: commonFormatMessage('total', {
                          total: data?.total_count
                        }),
                        stateTextVisible: data?.total_count > 10,
                        pageSizeVisible: data?.total_count > 10,
                        pageIndexVisible: data?.total_count > 10,
                        jumpVisible: data?.total_count > 10,
                        endJumpVisible: data?.total_count > 10,
                        onPagingChange: (query: PagingQuery) => {
                          setPageInfo(query);
                          // 请求表格数据
                          getTableListData(query);
                        }
                      }),
                      sortable({
                        columns: [{ key: 'create_time', prefer: 'desc' }],
                        value: sort,
                        onChange: (val: any[]) => {
                          setSort(val);
                          getTableListData(pageInfo, val);
                        }
                      }),
                      scrollable({
                        minWidth: 1200
                      }),
                      autotip({
                        isLoading: countInitLoadingRef.current || loading,
                        isError: !!error,
                        isFound: Boolean(searchValue) && data?.total_count === 0, // 搜索是否有结果
                        foundText: commonFormatMessage('not_search'),
                        onClear: () => {
                          // 搜索结果为空时, 显示返回原列表按钮, 同时设置按钮功能
                          clearSearchWords();
                          initTableParams();
                          getTableListData(
                            {pageSize: pageInfo.pageSize, pageIndex: 1},
                            sort,
                            '',
                            dashboardTab,
                            [],
                            [],
                          );
                        },
                        style: { lineHeight: '200px' },
                        errorText: (
                          <span className='tea-action-state__text tea-text-danger' title={commonFormatMessage('load_fail')}>
                            {commonFormatMessage('load_fail')}
                            <Text
                              theme='primary'
                              onClick={() => {
                                window.location.reload(true);
                              }}
                              style={{ cursor: 'pointer' }}
                            >
                              {commonFormatMessage('refresh')}
                            </Text>
                          </span>
                        )
                      })
                    ]}
                  />
                </ConfigProvider>
                
              </Card.Body>
            </Card>
            {showPublishModal && (
              <PublishModal
                publishId={publishRecord?.id}
                dashboardName={publishRecord?.dashboard_title}
                refreshTableListData={refreshTableListData}
                closeFunc={() => {
                  setShowPublishModal(false);
                }}
              />
            )}
            {showUnPublishModal && (
              <UnPublishDashboardModal
                record={unPublishRecord}
                confirmFunc={unPublishDashboardFunc}
                closeFunc={() => {
                  setShowUnPublishModal(false);
                }}
              />
            )}
            {showDeleteModal && (
              <DeleteDashboardModal
                closeFunc={() => {
                  setShowDeleteModal(false);
                }}
                deleteRecord={deleteRecord}
                deletedFunc={deleteDashboardFunc}
              />
            )}
            <AccessSettingModal
              visible={accessInfo.id !== -1}
              onClose={isSuccess => {
                setAccessInfo({ id: -1, editors: [], creatorId: '' });
                if (isSuccess) {
                  refreshTableListData();
                }
              }}
              id={accessInfo?.id}
              creatorId={accessInfo?.creatorId}
              editors={accessInfo?.editors}
              type='dashboard'
            />
            <SetAsTemplateModal visible={showSetTemplateModal} refreshTabCountData={refreshTabCountData} record={templateRecord}  gameCode={gameCode} onClose={()=>setShowSetTemplateModal(false)} ></SetAsTemplateModal>

            <CopyDashboardModal
              gameCode={gameCode}
              dashboardId={copyDashboard?.id}
              visible={showCopy}
              onClose={()=>setShowCopy(false)}
              dashboardName={copyDashboard?.dashboard_title || copyDashboard?.template_title}
              chartCopyType={copyDashboard?.category === TableDataTab.Public ? ChartInDashboardCopyType.COPY : ChartInDashboardCopyType.REFER}
              onSuccess={() => {
                // 更新数量显示
                refreshTabCountData();

                // 全部报表 和  我的报表 分类下 复制报表后 自动刷新 表格数据, 其他不刷新
                (dashboardTab === TableDataTab.MyDashboard || dashboardTab === TableDataTab.Dashboard) && refreshTableListData();
              }}
              showContact={copyDashboard?.show_contact}
            />
          </Content.Body>
        </Content>
      </Body>
    </Layout>
  );
}

export default MyDashboard;

interface IAuthPermissionSelectorState {
  privilegesConfig: {
    allPrivileges: string[];
  };
}

/**
 * @param type 权限标识
 * @param customDisabled 自定义权限，在已有权限基础上再增加判断
 * @param showNoPermissionBubble 无 type 权限且按钮禁用时，展示查看者无编辑权限说明
 * @description 统一包装权限按钮，集中细粒度处理,优先级会直接覆盖children的disabled属性，只针对禁用属性定制
 */
export const AuthPermissionDisablesContainers = ({
  children,
  type,
  customDisabled,
  isShow = false,
  showNoPermissionBubble = false,
}: {
  children: React.ReactNode;
  type: DATALAB_EDITOR;
  customDisabled?: boolean;
  isShow?: boolean;
  showNoPermissionBubble?: boolean;
}) => {
  const { formatMessage } = useIntl();
  const { allPrivileges } = useSelector((state: IAuthPermissionSelectorState) => ({
    allPrivileges: state.privilegesConfig.allPrivileges,
  }));
  const hasPrivilege = allPrivileges?.includes(type);
  const isDisabled = !hasPrivilege || customDisabled;
  // 无权限的情况，是否隐藏
  if (!hasPrivilege && isShow) {
    return <></>;
  }

  const noPermissionBubbleContent =
    showNoPermissionBubble && !hasPrivilege ? (
      <div>
        <p>{formatMessage({ id: 'dbl_common_no_permission' })}</p>
        <p>{formatMessage({ id: 'dbl_common_no_permission_desc' })}</p>
      </div>
    ) : undefined;

  return (
    <>
      {React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) {
          return null;
        }
        const cloned = React.cloneElement(child, { disabled: isDisabled });
        if (!noPermissionBubbleContent) {
          return cloned;
        }
        // disabled 按钮不响应 hover，需外层 span 承载 Bubble
        return (
          <Bubble content={noPermissionBubbleContent}>
            <span style={{ display: 'inline-block' }}>{cloned}</span>
          </Bubble>
        );
      })}
    </>
  );
};